import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { ClubeApi } from "../../../hooks/clubeApi";
import { ContainerAula } from "./styles";
import { ClubeAula, ClubeMaterial } from "../../../types/Clube";
import { carregarYoutubeApi, extrairVideoId } from "./youtube";

// Tela da aula: player do YouTube (IFrame API) com cálculo de % assistida,
// salvamento de progresso e botão "Próximo conteúdo" pela ordenação.
const Aula: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const clubeApi = ClubeApi();

  const [aula, setAula] = useState<ClubeAula | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [pct, setPct] = useState(0);
  // URL do PDF aberto no lugar do vídeo (null = mostrando o vídeo)
  const [pdfAtivo, setPdfAtivo] = useState<string | null>(null);

  const playerRef = useRef<any>(null);
  const intervalRef = useRef<any>(null);
  const pctRef = useRef(0); // maior % já atingida na sessão
  const salvoRef = useRef(0); // última % enviada ao backend
  const idAulaRef = useRef<number | null>(null);

  // Envia a % ao backend (sem regredir / sem repetir o mesmo valor).
  const salvarProgresso = (valor: number) => {
    const aulaId = idAulaRef.current;
    if (!aulaId) return;
    if (valor <= salvoRef.current) return;
    salvoRef.current = valor;
    clubeApi.salvarProgresso(aulaId, valor).catch(() => {});
  };

  // Carrega a aula. Refaz quando muda o :id (ex.: "próximo conteúdo").
  useEffect(() => {
    if (!id) return;
    setLoaded(false);
    setPct(0);
    setPdfAtivo(null);
    pctRef.current = 0;
    salvoRef.current = 0;
    idAulaRef.current = Number(id);
    clubeApi.registrarEvento("abrir_aula", Number(id));

    clubeApi
      .getAula(Number(id))
      .then((res) => {
        const dados: ClubeAula | null = res?.aula || null;
        setAula(dados);
        const inicial = Number(dados?.porcentagem || 0);
        pctRef.current = inicial;
        salvoRef.current = inicial;
        setPct(inicial);

        // Sem vídeo: abre o primeiro PDF direto no lugar do player.
        if (!extrairVideoId(dados?.youtube_url)) {
          const primeiroPdf = (dados?.materiais || []).find(
            (m) =>
              (m.nome || m.arquivo || "").split(".").pop()?.toLowerCase() ===
              "pdf"
          );
          if (primeiroPdf) setPdfAtivo(primeiroPdf.arquivo);
        }
      })
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Inicializa / recria o player do YouTube quando a aula carrega.
  useEffect(() => {
    if (!aula) return;
    const videoId = extrairVideoId(aula.youtube_url);
    if (!videoId) return;

    let destruido = false;

    const atualizar = () => {
      const player = playerRef.current;
      if (!player || typeof player.getDuration !== "function") return;
      const dur = player.getDuration();
      const atual = player.getCurrentTime();
      if (!dur || dur <= 0) return;
      const novaPct = Math.min(100, Math.round((atual / dur) * 100));
      if (novaPct > pctRef.current) {
        pctRef.current = novaPct;
        setPct(novaPct);
      }
    };

    carregarYoutubeApi().then((YT) => {
      if (destruido) return;
      playerRef.current = new YT.Player(`yt-player-${aula.id}`, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: (e: any) => {
            // retoma de onde parou (se entre 1% e 95%)
            const inicial = pctRef.current;
            if (inicial > 1 && inicial < 95) {
              const dur = e.target.getDuration();
              if (dur > 0) e.target.seekTo((dur * inicial) / 100, true);
            }
          },
          onStateChange: (e: any) => {
            // 1 = playing, 2 = paused, 0 = ended
            if (e.data === 1) {
              if (!intervalRef.current) {
                intervalRef.current = setInterval(atualizar, 5000);
              }
            } else {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
              atualizar();
              if (e.data === 0) {
                pctRef.current = 100;
                setPct(100);
              }
              salvarProgresso(pctRef.current);
            }
          },
        },
      });
    });

    return () => {
      destruido = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      salvarProgresso(pctRef.current);
      if (playerRef.current && typeof playerRef.current.destroy === "function") {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aula?.id]);

  if (!loaded) return <div style={{ padding: "4rem 10rem" }}>Carregando...</div>;
  if (!aula)
    return <div style={{ padding: "4rem 10rem" }}>Aula não encontrada.</div>;

  const proxima = aula.proxima_aula;
  const materiais = aula.materiais || [];
  // Aula pode não ter vídeo (ex.: só descrição com links). Sem vídeo, não
  // mostramos o player nem a barra de progresso.
  const temVideo = !!extrairVideoId(aula.youtube_url);

  // helpers por material (extensão / ícone / é PDF)
  const extDe = (m: ClubeMaterial) =>
    (m.nome || m.arquivo || "").split(".").pop()?.toLowerCase();
  const ehPdf = (m: ClubeMaterial) => extDe(m) === "pdf";
  const iconeDe = (m: ClubeMaterial) => {
    const e = extDe(m);
    return e === "xlsx" || e === "xls" ? "📊" : "📄";
  };

  // Renderiza a descrição convertendo links em markdown -> [texto](url)
  // em <a> clicável, mostrando só o texto personalizado (a URL fica oculta).
  // O resto vira texto normal (quebras de linha preservadas via CSS).
  const renderDescricao = (texto: string) => {
    const partes: React.ReactNode[] = [];
    const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    let ultimo = 0;
    let chave = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(texto)) !== null) {
      if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
      partes.push(
        <a
          key={`lnk-${chave++}`}
          href={m[2]}
          target="_blank"
          rel="noreferrer"
          className="link-descricao"
        >
          {m[1]}
        </a>
      );
      ultimo = m.index + m[0].length;
    }
    if (ultimo < texto.length) partes.push(texto.slice(ultimo));
    return partes;
  };

  return (
    <ContainerAula theme={theme}>
      <div className="topo-progresso">
        {aula.titulo && (
          <strong className="contexto fonteTitulo">{aula.titulo}</strong>
        )}
        {temVideo && (
          <div className="barra">
            <div className="trilho">
              <div className="preenchido" style={{ width: `${pct}%` }} />
            </div>
            <div className="pct fonteTexto">{pct}%</div>
          </div>
        )}
      </div>

      {(temVideo || pdfAtivo) && (
        <div className={`player-wrapper${pdfAtivo ? " modo-pdf" : ""}`}>
          {temVideo && (
            <div
              className="video-box"
              style={{ display: pdfAtivo ? "none" : "block" }}
            >
              <div id={`yt-player-${aula.id}`} className="yt-player" />
            </div>
          )}
          {pdfAtivo && (
            <iframe
              className="material-frame"
              src={pdfAtivo}
              title="Material complementar"
            />
          )}
        </div>
      )}

      <div className="aula-conteudo">
        <div className="aula-info">
          <strong className="fonteTitulo">{aula.trilha_titulo}</strong>
          {aula.descricao && (
            <div className="descricao fonteTexto">
              {renderDescricao(aula.descricao)}
            </div>
          )}
        </div>

        {proxima && (
          <div
            className="proximo-conteudo"
            onClick={() =>
              navigate(
                `/clube-conectado/trilha/${aula.id_trilha}/${encodeURIComponent(
                  aula.trilha_titulo || ""
                )}/aula/${proxima.id}/${encodeURIComponent(proxima.titulo)}`
              )
            }
          >
            <div className="texto">
              <span className="rotulo fonteRotulos">
                Próximo conteúdo
                {aula.trilha_titulo ? ` | ${aula.trilha_titulo}` : ""}
              </span>
              <span className="nome fonteTexto">{proxima.titulo}</span>
            </div>
            <div className="seta">›</div>
          </div>
        )}

        {materiais.length > 0 && (
        <div className="materiais">
          <strong className="fonteTitulo">Material complementar</strong>
          <div className="lista-materiais">
            {materiais.map((m) =>
              ehPdf(m) ? (
                <button
                  key={m.id}
                  type="button"
                  className={`material-item fonteTexto${
                    pdfAtivo === m.arquivo ? " ativo" : ""
                  }`}
                  onClick={() => {
                    const abrindo = pdfAtivo !== m.arquivo;
                    if (abrindo)
                      clubeApi.registrarEvento("abrir_material", aula.id);
                    // pausa o vídeo ao abrir o PDF (display:none não pausa o YT)
                    if (abrindo && playerRef.current?.pauseVideo) {
                      playerRef.current.pauseVideo();
                    }
                    // sem vídeo, manter o PDF sempre aberto (não há player p/ voltar)
                    if (!abrindo && !temVideo) return;
                    setPdfAtivo(abrindo ? m.arquivo : null);
                  }}
                >
                  {pdfAtivo === m.arquivo
                    ? temVideo
                      ? "▶ Voltar ao vídeo"
                      : `${iconeDe(m)} ${m.nome || "Material"} (aberto)`
                    : `${iconeDe(m)} ${m.nome || "Abrir material"}`}
                </button>
              ) : (
                <a
                  key={m.id}
                  href={m.arquivo}
                  target="_blank"
                  rel="noreferrer"
                  className="material-item fonteTexto"
                  onClick={() =>
                    clubeApi.registrarEvento("abrir_material", aula.id)
                  }
                >
                  {iconeDe(m)} {m.nome || "Abrir material"}
                </a>
              )
            )}
          </div>
        </div>
        )}
      </div>
    </ContainerAula>
  );
};

export default Aula;
